import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../lib/supabase";
import {
  Send,
  ThumbsUp,
  MessageCircle,
  Clock,
  MoreHorizontal,
  Image,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import CampusComments from "./CampusComments";

const CampusFeed = ({
  communityId,
  userAlias,
  userId,
  userCampus,
  userAvatar,
  feedMode,
  onSetFeedMode,
  onViewProfile,
}) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [newPostContent, setNewPostContent] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCommentsForPost, setShowCommentsForPost] = useState(null);
  const [failedAvatars, setFailedAvatars] = useState(new Set());
  const fileInputRef = useRef(null);

  const POSTS_PER_PAGE = 20;

  // Helper to map string IDs to integer IDs
  const getCommunityIdCode = (id) => {
    const map = {
      feed: 1,
      qa: 2,
      confessions: 3,
      lost: 4,
      study: 5,
    };
    return map[id] || 1;
  };

  // DEBUG: Log posts with avatar data
  useEffect(() => {
    if (posts.length > 0) {
      console.log(
        "Current posts in state:",
        posts.map((p) => ({
          id: p.id,
          author: p.author_alias,
          profiles: p.profiles,
          avatar_url: p.profiles?.avatar_url,
        }))
      );
    }
  }, [posts]);

  useEffect(() => {
    // Reset posts when community or feedMode changes
    setPosts([]);
    setPage(0);
    setHasMore(true);
    fetchPosts(0, true);

    // Real-time subscription
    const channel = supabase
      .channel("public:posts")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "posts" },
        (payload) => {
          const isUnified = communityId === "feed";
          const targetId = getCommunityIdCode(communityId);

          if (payload.eventType === "INSERT") {
            const isQAPost = payload.new.community_id === 2;
            const isCorrectCampus =
              feedMode === "local"
                ? payload.new.campus_code === userCampus
                : true;

            if (
              ((isUnified && !isQAPost) ||
                (!isUnified && payload.new.community_id === targetId)) &&
              isCorrectCampus
            ) {
              // Fetch the author profile
              supabase
                .from("profiles")
                .select("avatar_url, campus_alias")
                .eq("id", payload.new.author_id)
                .single()
                .then(({ data, error }) => {
                  if (error) {
                    console.error("Error fetching profile:", error);
                  }
                  const newPost = {
                    ...payload.new,
                    profiles: data
                      ? {
                          avatar_url: data.avatar_url,
                          campus_alias: data.campus_alias,
                        }
                      : { avatar_url: null, campus_alias: null },
                  };
                  setPosts((prev) => {
                    if (prev.find((p) => p.id === newPost.id)) return prev;
                    return [newPost, ...prev];
                  });
                })
                .catch((err) => console.error("Profile fetch error:", err));
            }
          } else if (payload.eventType === "UPDATE") {
            setPosts((prev) =>
              prev.map((post) =>
                post.id === payload.new.id
                  ? { ...post, ...payload.new, profiles: post.profiles }
                  : post
              )
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [communityId, feedMode, userCampus]);

  const fetchPosts = async (pageNumber = 0, isReset = false) => {
    if (pageNumber === 0) setLoading(true);

    const from = pageNumber * POSTS_PER_PAGE;
    const to = from + POSTS_PER_PAGE - 1;

    // First, fetch posts
    let query = supabase
      .from("posts")
      .select("*")
      .order("created_at", { ascending: false })
      .range(from, to);

    // Community Filter
    if (communityId !== "feed") {
      query = query.eq("community_id", getCommunityIdCode(communityId));
    } else {
      query = query.neq("community_id", 2);
    }

    // Dual-Feed Filter (My Campus vs Unified)
    if (feedMode === "local" && userCampus) {
      query = query.eq("campus_code", userCampus);
    }

    const { data: postsData, error: postsError } = await query;

    if (postsError) {
      console.error("Error fetching posts:", postsError);
      setLoading(false);
      return;
    }

    let newPosts = postsData || [];
    console.log("Fetched posts:", newPosts); // DEBUG

    // Fetch profiles for all posts
    if (newPosts.length > 0) {
      const authorIds = [...new Set(newPosts.map((p) => p.author_id))];
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, avatar_url, campus_alias")
        .in("id", authorIds);

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
      } else {
        console.log("Fetched profiles:", profilesData); // DEBUG
        const profileMap = {};
        if (profilesData) {
          profilesData.forEach((profile) => {
            profileMap[profile.id] = profile;
          });
        }

        // Attach profiles to posts
        newPosts = newPosts.map((post) => ({
          ...post,
          profiles: profileMap[post.author_id] || {
            avatar_url: null,
            campus_alias: null,
          },
        }));
      }

      if (newPosts.length < POSTS_PER_PAGE) {
        setHasMore(false);
      }

      if (isReset) {
        setPosts(newPosts);
      } else {
        setPosts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const uniqueNewPosts = newPosts.filter((p) => !existingIds.has(p.id));
          return [...prev, ...uniqueNewPosts];
        });
      }
    }
    setLoading(false);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Security: Client-side Validation
    if (file.size > 5 * 1024 * 1024) {
      // 5MB limit
      toast.error("Image size must be less than 5MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePost = async () => {
    if (!newPostContent.trim() && !imageFile) return;
    setIsPosting(true);

    let uploadedImageUrl = null;
    let optimisticPost = null;

    // 0. Optimistic Update (Immediate Feedback)
    if (!imageFile) {
      optimisticPost = {
        id: Date.now(), // Temporary ID
        content: newPostContent,
        community_id:
          communityId === "feed" ? 1 : getCommunityIdCode(communityId),
        author_id: userId,
        author_alias: userAlias,
        campus_code: userCampus, // Tag optimistic post with current campus
        created_at: new Date().toISOString(),
        upvotes: 0,
        profiles: { avatar_url: null },
        isOptimistic: true,
      };
      setPosts((prev) => [optimisticPost, ...prev]);
      setNewPostContent("");
    }

    // 1. Image Upload (If exists)
    if (imageFile) {
      const fileExt = imageFile.name.split(".").pop();
      const fileName = `${Date.now()}_${Math.random()
        .toString(36)
        .substring(2)}.${fileExt}`;
      const filePath = `post-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("post-images")
        .upload(filePath, imageFile);

      if (uploadError) {
        console.error("Upload error:", uploadError);
        toast.error("Failed to upload image.");
        setIsPosting(false);
        return;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("post-images").getPublicUrl(filePath);
      uploadedImageUrl = publicUrl;
    }

    // 2. Insert Post
    const { error } = await supabase.from("posts").insert([
      {
        content: optimisticPost ? optimisticPost.content : newPostContent,
        image_url: uploadedImageUrl,
        community_id:
          communityId === "feed" ? 1 : getCommunityIdCode(communityId),
        author_id: userId,
        author_alias: userAlias,
        campus_code: userCampus, // TAG POST WITH CAMPUS
      },
    ]);

    if (error) {
      console.error(error);

      // Handle Rate Limit Error specifically
      if (error.message && error.message.includes("Rate Limit Exceeded")) {
        // Extract the friendly message if possible, or just show the standard one
        toast.warning(
          "Daily Limit Reached: You can only post 7 times in 24 hours."
        );
      } else {
        toast.error("Failed to post: " + (error.message || "Unknown error"));
      }

      // Rollback optimistic post
      if (optimisticPost) {
        setPosts((prev) => prev.filter((p) => p.id !== optimisticPost.id));
        setNewPostContent(optimisticPost.content); // Restore content
      }
    } else {
      if (imageFile) {
        // If we had an image, we didn't do optimistic update earlier, do clear now
        setNewPostContent("");
      }
      clearImage();
      toast.success("Posted anonymously!");
    }
    setIsPosting(false);
  };

  const handleLike = async (postId, currentUpvotes) => {
    // 1. Optimistic Update
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, upvotes: (p.upvotes || 0) + 1 } : p
      )
    );

    // 2. Secure Server Call
    const { data: success, error } = await supabase.rpc("vote_for_post", {
      post_id_param: postId,
      user_id_param: userId,
    });

    // 3. Handle Failure (Already voted or Error)
    if (error || success === false) {
      if (success === false) {
        toast.info("You've already liked this post!");
      } else {
        console.error("Like failed:", error);
        toast.error("Could not save like.");
      }

      // Revert optimistic update
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, upvotes: currentUpvotes } : p
        )
      );
    }
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return "Just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="max-w-3xl mx-auto pb-16 sm:pb-20">
      {/* Post Composer - Redesigned & Elevated */}
      <div
        className={`glass-card p-3 sm:p-4 dark:bg-white/5 dark:border-indigo-500/20 dark:backdrop-blur-sm transition-all duration-500 overflow-hidden mb-6 sm:mb-8 ${
          isExpanded ? "ring-2 ring-indigo-500/30" : ""
        }`}
      >
        <div className="flex gap-3 sm:gap-4">
          {userAvatar ? (
            <img
              src={userAvatar}
              alt={userAlias}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-cover shrink-0 border border-indigo-200 dark:border-indigo-400"
            />
          ) : (
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-slate-900 flex items-center justify-center text-white font-bold border border-slate-200 shrink-0 text-sm">
              {userAlias?.[0]}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <textarea
              value={newPostContent}
              onFocus={() => setIsExpanded(true)}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder={`What's on your mind, ${userAlias}?`}
              className={`w-full bg-transparent border-none rounded-xl p-0 resize-none outline-none text-[14px] sm:text-[15px] text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 transition-all duration-300 ${
                isExpanded
                  ? "min-h-[100px] sm:min-h-[120px] mt-1.5 sm:mt-2"
                  : "min-h-[36px] sm:min-h-[40px] pt-2"
              }`}
            />
          </div>
        </div>

        {/* Expanded Options */}
        <div
          className={`grid transition-all duration-500 overflow-hidden ${
            isExpanded
              ? "grid-rows-[1fr] opacity-100 mt-4 sm:mt-6"
              : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            {/* Image Preview */}
            {imagePreview && (
              <div className="relative mb-4 sm:mb-6 rounded-2xl overflow-hidden border border-slate-100 dark:border-indigo-500/20 shadow-sm dark:shadow-lg dark:shadow-indigo-600/20">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-32 sm:h-48 object-cover"
                />
                <button
                  onClick={clearImage}
                  className="absolute top-2 right-2 bg-slate-900/80 dark:bg-slate-900/90 text-white rounded-full p-1 sm:p-1.5 backdrop-blur-md hover:bg-slate-900 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            <div className="flex justify-between items-center pt-3 sm:pt-4 border-t border-slate-100 dark:border-indigo-500/10">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 text-[11px] sm:text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-300 bg-slate-50 dark:bg-white/5 rounded-xl transition-all"
                >
                  <Image size={14} className="sm:w-4 sm:h-4" />
                  Media
                </button>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-[11px] sm:text-xs font-bold text-slate-400 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-all"
                >
                  Cancel
                </button>
              </div>

              <button
                onClick={handlePost}
                disabled={isPosting || (!newPostContent.trim() && !imageFile)}
                className="bg-slate-900 text-white px-6 sm:px-8 py-2 sm:py-2.5 rounded-xl font-bold text-[11px] sm:text-xs flex items-center gap-1.5 sm:gap-2 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all uppercase tracking-widest shadow-lg shadow-slate-900/10"
              >
                {isPosting ? "Posting..." : "Share"}
                <Send size={13} className="sm:w-3.5 sm:h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Feed Toggle - Segmented Control */}
      {communityId === "feed" && (
        <div className="relative flex bg-slate-100 dark:bg-white/5 dark:backdrop-blur-sm p-1 sm:p-1.5 rounded-2xl mb-8 sm:mb-10 w-full max-w-[280px] sm:max-w-[320px] mx-auto border border-slate-200/50 dark:border-indigo-500/20 shadow-sm">
          {/* Sliding Pill Background */}
          <div
            className={`absolute inset-y-1.5 w-[calc(50%-6px)] bg-white dark:bg-indigo-600/20 rounded-xl shadow-sm transition-all duration-300 ease-out z-0 ${
              feedMode === "local" ? "translate-x-full ml-1.5" : "translate-x-0"
            }`}
          />

          <button
            onClick={() => onSetFeedMode("unified")}
            className={`flex-1 relative z-10 py-2.5 rounded-xl text-xs font-black transition-all uppercase tracking-widest ${
              feedMode === "unified"
                ? "text-indigo-600 dark:text-indigo-300"
                : "text-slate-400 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            }`}
          >
            Unified Feed
          </button>
          <button
            onClick={() => onSetFeedMode("local")}
            className={`flex-1 relative z-10 py-2.5 rounded-xl text-xs font-black transition-all uppercase tracking-widest ${
              feedMode === "local"
                ? "text-indigo-600 dark:text-indigo-300"
                : "text-slate-400 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            }`}
          >
            My Campus
          </button>
        </div>
      )}

      {/* Feed */}
      {loading && page === 0 ? (
        <div className="space-y-3 sm:space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white dark:bg-white/5 dark:backdrop-blur-sm h-32 sm:h-48 rounded-xl animate-pulse dark:border dark:border-indigo-500/10"
            />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-8 sm:py-12 text-gray-500 dark:text-slate-400 bg-white dark:bg-white/5 dark:backdrop-blur-sm dark:border dark:border-indigo-500/20 rounded-xl border border-gray-100 border-dashed">
          <MessageCircle
            size={40}
            className="sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 opacity-20"
          />
          <p className="text-sm sm:text-base">
            No posts found here yet. Be the first!
          </p>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {posts.map((post) => (
            <div
              key={post.id}
              className="glass-card premium-shadow p-4 sm:p-6 dark:bg-white/5 dark:border-indigo-500/20 dark:backdrop-blur-sm transition-all hover:scale-[1.01] duration-300"
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-3 sm:mb-4">
                <div
                  className="flex items-center gap-2.5 sm:gap-3 group cursor-pointer flex-1 min-w-0"
                  onClick={() => onViewProfile && onViewProfile(post.author_id)}
                >
                  <div className="shrink-0 transition-transform duration-500 group-hover:rotate-6">
                    {post.profiles?.avatar_url ? (
                      <img
                        src={post.profiles.avatar_url}
                        alt={post.author_alias}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl object-cover border border-slate-100 dark:border-indigo-500/30 shadow-sm"
                        loading="eager"
                        onError={(e) => {
                          console.error(
                            `Image failed to load for post ${post.id}: ${post.profiles?.avatar_url}`,
                            e
                          );
                          setFailedAvatars(
                            (prev) => new Set([...prev, post.id])
                          );
                        }}
                        onLoad={() =>
                          console.log(`Avatar loaded for post ${post.id}`)
                        }
                      />
                    ) : (
                      <div
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-sm sm:text-lg font-black shadow-sm ${
                          post.author_id === userId
                            ? "bg-slate-900 text-white"
                            : "bg-slate-50 text-slate-400 border border-slate-200"
                        }`}
                      >
                        {post.author_alias?.charAt(0) || "?"}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-black text-sm sm:text-base text-slate-900 dark:text-white leading-tight transition-colors">
                        {post.author_alias}
                      </h4>
                      {post.campus_code && (
                        <span
                          className={`text-[8px] sm:text-[9px] px-1.5 sm:px-2 py-0.5 rounded-lg font-black uppercase tracking-[0.1em] whitespace-nowrap ${
                            post.campus_code === userCampus
                              ? "bg-indigo-50 dark:bg-indigo-500/30 text-indigo-600 dark:text-indigo-300"
                              : "bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-slate-400"
                          }`}
                        >
                          {post.campus_code}
                        </span>
                      )}
                    </div>
                    <span className="text-[9px] sm:text-[10px] text-slate-400 dark:text-slate-400 font-bold uppercase tracking-widest mt-0.5 sm:mt-1 block">
                      {getTimeAgo(post.created_at)}
                    </span>
                  </div>
                </div>
                <button className="text-slate-300 dark:text-slate-500 hover:text-slate-500 dark:hover:text-slate-400 p-1.5 sm:p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-white/10 transition-all shrink-0">
                  <MoreHorizontal size={18} className="sm:w-5 sm:h-5" />
                </button>
              </div>

              <div className="space-y-2.5 sm:space-y-3">
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap text-[13px] sm:text-[14px] font-medium">
                  {post.content}
                </p>

                {post.image_url && (
                  <div className="relative rounded-[1.5rem] overflow-hidden border border-slate-100 dark:border-indigo-500/20 shadow-inner bg-slate-50 dark:bg-indigo-500/10">
                    <img
                      src={post.image_url}
                      alt="Post attachment"
                      className="w-full h-auto max-h-[400px] sm:max-h-[500px] object-cover cursor-pointer hover:scale-[1.02] transition-transform duration-700"
                      loading="lazy"
                      onClick={() => setSelectedImage(post.image_url)}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1 mt-5 sm:mt-6 pt-3 sm:pt-4 border-t border-slate-100 dark:border-indigo-500/10">
                <button
                  onClick={() => handleLike(post.id, post.upvotes)}
                  className={`flex items-center gap-1.5 sm:gap-2.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-[11px] sm:text-xs font-black transition-all group ${
                    post.hasVoted
                      ? "text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/20"
                      : "text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-300 hover:bg-slate-50 dark:hover:bg-white/5"
                  }`}
                >
                  <ThumbsUp
                    size={16}
                    className="sm:w-[18px] sm:h-[18px] group-active:scale-125 transition-transform"
                  />
                  <span>{post.upvotes || 0}</span>
                </button>

                <button
                  onClick={() => setShowCommentsForPost(post.id)}
                  className="flex items-center gap-1.5 sm:gap-2.5 px-3 sm:px-4 py-1.5 sm:py-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-300 text-[11px] sm:text-xs font-black transition-all hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl group"
                >
                  <MessageCircle
                    size={16}
                    className="sm:w-[18px] sm:h-[18px] group-hover:rotate-[-10deg] transition-transform"
                  />
                  <span>COMMENTS</span>
                </button>
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center pt-3 sm:pt-4">
              <button
                onClick={loadMore}
                className="bg-white dark:bg-white/10 dark:backdrop-blur-sm dark:border-indigo-500/20 border border-gray-200 dark:border-indigo-500/20 text-gray-600 dark:text-slate-300 px-5 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/20 hover:text-gray-900 dark:hover:text-white transition-colors shadow-sm"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer backdrop-blur-sm"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <img
            src={selectedImage}
            alt="Full view"
            className="max-w-full max-h-[90vh] rounded-lg shadow-2xl object-contain cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Comments Modal */}
      {showCommentsForPost && (
        <CampusComments
          postId={showCommentsForPost}
          onClose={() => setShowCommentsForPost(null)}
          currentUser={{ id: userId, alias: userAlias }}
          onViewProfile={(uid) => {
            setShowCommentsForPost(null); // Close modal
            onViewProfile && onViewProfile(uid); // Nav to profile
          }}
        />
      )}
    </div>
  );
};

export default CampusFeed;
